import { useState } from "react";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const OptInForm = ({
  formId,
  heading,
  subheading,
  source,
  fields,
  buttonLabel,
  thankYou,
  testidPrefix,
}) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/subscribe", { ...values, source });
      setDone(true);
      toast.success(thankYou);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-taupe outline-none transition-colors focus:border-gold";

  return (
    <div
      className="mx-auto w-full max-w-xl rounded-3xl border border-line bg-white p-8 shadow-xl sm:p-10"
      data-testid={`${testidPrefix}-card`}
    >
      {done ? (
        <div
          className="flex flex-col items-center gap-4 py-6 text-center"
          data-testid={`${testidPrefix}-success`}
        >
          <Heart className="h-10 w-10 text-gold" fill="currentColor" />
          <p className="font-serif text-xl italic leading-snug text-ink sm:text-2xl">
            {thankYou}
          </p>
        </div>
      ) : (
        <>
          <h2 className="text-center font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {heading}
          </h2>
          {subheading && (
            <p className="mt-2 text-center text-sm leading-relaxed text-cocoa">
              {subheading}
            </p>
          )}
          <form
            id={formId}
            onSubmit={submit}
            className="mt-8"
            data-testid={`${testidPrefix}-form`}
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <label
                    htmlFor={`${formId}-${f.name}`}
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-cocoa"
                  >
                    {f.label}
                  </label>
                  {f.textarea ? (
                    <textarea
                      id={`${formId}-${f.name}`}
                      name={f.name}
                      required
                      rows={4}
                      value={values[f.name] || ""}
                      onChange={set(f.name)}
                      placeholder={f.placeholder || ""}
                      className={inputCls}
                      data-testid={`${testidPrefix}-${f.name.replace(/_/g, "-")}-input`}
                    />
                  ) : (
                    <input
                      id={`${formId}-${f.name}`}
                      name={f.name}
                      type={f.type || "text"}
                      required
                      value={values[f.name] || ""}
                      onChange={set(f.name)}
                      placeholder={f.placeholder || ""}
                      className={inputCls}
                      data-testid={`${testidPrefix}-${f.name.replace(/_/g, "-")}-input`}
                    />
                  )}
                </div>
              ))}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep disabled:opacity-60"
              data-testid={`${testidPrefix}-submit-button`}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {buttonLabel}
            </button>
          </form>
        </>
      )}
    </div>
  );
};
